import { useReadOnly } from '@/lib/readOnly';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cx } from '@/lib/cx';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Leading icon (a lucide icon element). */
  icon?: ReactNode;
  /** Trailing icon. */
  iconRight?: ReactNode;
  block?: boolean;
}

export interface ButtonProps extends CommonProps, ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  /** Replaces the label while loading, e.g. "Saving…". */
  loadingText?: string;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Button. Use one primary per view region. `danger` only for destructive confirmation.
 * While `loading`, the button stays focusable, announces busy state and ignores clicks.
 */
export function Button({ variant = 'secondary', size = 'md', icon, iconRight, block, loading, loadingText, className, children, disabled, type = 'button', onClick, ref, ...rest }: ButtonProps) {
  const readOnly = useReadOnly();
  const writeBlocked = readOnly && (type === 'submit' || variant === 'danger' || variant === 'danger-ghost');
  return (
    <button
      ref={ref}
      type={type}
      className={cx('btn', `btn-${variant}`, `btn-${size}`, block && 'btn-block', loading && 'is-loading', className)}
      disabled={disabled || writeBlocked}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={(e) => {
        if (loading) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 12 : 14} /> : icon ? <span className="btn-icon" aria-hidden>{icon}</span> : null}
      {children !== undefined && <span className="btn-label">{loading && loadingText ? loadingText : children}</span>}
      {iconRight && !loading && <span className="btn-icon" aria-hidden>{iconRight}</span>}
    </button>
  );
}

export interface ButtonLinkProps extends CommonProps, LinkProps {}

/** Navigation styled as a button (renders an anchor). */
export function ButtonLink({ variant = 'secondary', size = 'md', icon, iconRight, block, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={cx('btn', `btn-${variant}`, `btn-${size}`, block && 'btn-block', className)} {...rest}>
      {icon && <span className="btn-icon" aria-hidden>{icon}</span>}
      <span className="btn-label">{children}</span>
      {iconRight && <span className="btn-icon" aria-hidden>{iconRight}</span>}
    </Link>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name — required because the button has no visible text. */
  label: string;
  icon: ReactNode;
  variant?: 'ghost' | 'secondary' | 'danger-ghost' | 'primary';
  size?: ButtonSize;
  loading?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({ label, icon, variant = 'ghost', size = 'md', loading, className, type = 'button', ref, ...rest }: IconButtonProps) {
  return (
    <button ref={ref} type={type} aria-label={label} title={rest.title} className={cx('btn', 'btn-icon-only', `btn-${variant}`, `btn-${size}`, className)} aria-busy={loading || undefined} {...rest}>
      {loading ? <Spinner size={14} /> : <span aria-hidden className="btn-icon">{icon}</span>}
    </button>
  );
}
